"use client";
import { ClerkProvider, useUser } from "@clerk/nextjs";
import { ReactNode, useEffect, useRef } from "react";
import { getAuthTenantSettings } from "../auth-tenant-settings";
import { SyncOrganizationApproval } from "@/organizations/school/SyncOrganizationApproval";
import {
  identifyPostHogUser,
  isPostHogConfigured,
  resetPostHogIdentity,
} from "@/tracking/posthog/config";

function PostHogIdentitySync() {
  const { user, isLoaded } = useUser();
  const userId = user?.id;
  const email = user?.primaryEmailAddress?.emailAddress;
  const name = user?.fullName ?? undefined;
  const identifiedUserId = useRef<string | null>(null);

  useEffect(() => {
    if (!isLoaded || !isPostHogConfigured) return;

    if (!userId) {
      if (identifiedUserId.current) {
        resetPostHogIdentity();
        identifiedUserId.current = null;
      }
      return;
    }

    if (identifiedUserId.current === userId) return;

    if (identifiedUserId.current) {
      resetPostHogIdentity();
    }

    identifyPostHogUser({ id: userId, email, name });
    identifiedUserId.current = userId;
  }, [email, isLoaded, name, userId]);

  return null;
}

export function AuthProvider({
  children,
  universeTag,
}: {
  children: ReactNode;
  universeTag: string;
}) {
  const {
    allowedRedirectOrigins,
    isSatellite,
    signInUrl,
    signUpUrl,
    domain,
    satelliteAutoSync,
  } = getAuthTenantSettings(universeTag);

  const clerkSettings = isSatellite
    ? {
        isSatellite,
        domain,
        signInUrl,
        signUpUrl,
        satelliteAutoSync,
      }
    : { allowedRedirectOrigins };

  return (
    // Our custom UI and SSO callback do not need Clerk's component renderer.
    <ClerkProvider {...clerkSettings} prefetchUI={false}>
      <PostHogIdentitySync />
      <SyncOrganizationApproval />
      {children}
    </ClerkProvider>
  );
}
